import { useOrderSetBookingsResult } from "@repo/data-store";
import { createContext, type ReactNode, use, useEffect, useState } from "react";
import { resolveSchedulerInput, type SchedulerInput, type SchedulerInputResult } from "./resolve-scheduler-input";

type InputState = SchedulerInputResult | { status: "loading" };
const SchedulerInputContext = createContext<InputState | null>(null);

/** Hydrates IDs once per overlay; booking writes must not remove sessions mid-flow. */
export function SchedulerInputProvider({
  orderSetIds,
  children,
}: {
  orderSetIds: readonly string[];
  children: ReactNode;
}) {
  const [ids] = useState(() => [...new Set(orderSetIds)]);
  const result = useOrderSetBookingsResult({ ids });
  const [snapshot, setSnapshot] = useState<SchedulerInput | null>(null);
  const resolution: InputState =
    ids.length === 0
      ? resolveSchedulerInput(ids, [])
      : result.isError
        ? { status: "error", message: "Could not load the sessions. Close the scheduler and try again." }
        : !result.isReady
          ? { status: "loading" }
          : resolveSchedulerInput(ids, result.bookings);

  useEffect(() => {
    if (!snapshot && resolution.status === "ready") setSnapshot(resolution.data);
  }, [snapshot, resolution]);

  const state: InputState = snapshot
    ? { status: "ready", data: snapshot }
    : resolution.status === "ready"
      ? { status: "loading" }
      : resolution;
  return <SchedulerInputContext value={state}>{children}</SchedulerInputContext>;
}

export function useSchedulerInput() {
  const state = use(SchedulerInputContext);
  if (!state) throw new Error("Order-set scheduler must be rendered inside SchedulerInputProvider");
  return state;
}
